import type { FC } from 'react';

import classNames from 'classnames';

import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { selectLatestActiveVersionExtended } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import {
  selectIncompleteTransferJobByObjectId,
  selectStartedTranscodeJobsByObjectId,
} from '@iconik-io/shared-lib/src/redux/selectors/jobs/selectors';
import { Icon } from '@iconik-io/uad';

import { ARCHIVED } from '../../../constants/archive';
import { useAppSelector } from '../../../store/react_redux_typed';
import TooltipTrigger from '../../tooltip_trigger/tooltip_trigger';

import styles from '../asset_status_icon_detailed.module.css';

type Props = {
  object: SearchItemEntity;
};

const InfoStatuses: FC<Props> = ({ object }) => {
  const statuses = [];

  const startedTranscodeJobs = useAppSelector((state) =>
    selectStartedTranscodeJobsByObjectId(state, object.id),
  );

  const incompleteTransferJob = useAppSelector((state) =>
    selectIncompleteTransferJobByObjectId(state, object.id),
  );
  const latestVersion = useAppSelector((state) =>
    selectLatestActiveVersionExtended(state, object.id),
  );

  if (startedTranscodeJobs.length > 0) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Transcoding in progress"
        placement="bottom"
        key="transcode-progress-status"
        componentProps={{
          role: 'status',
          'aria-label': 'Transcoding in progress',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="transcoder" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (object.comments_count && object.comments_count > 0) {
    statuses.push(
      <div key="comment-status" className={styles.status_container}>
        <div className={styles.status_icon}>
          <Icon name="comment" />
        </div>
      </div>,
    );
  }

  if (object.archive_status === ARCHIVED) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Archived"
        key="archived-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Archived',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="archive_archived" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (object.face_recognition_status === 'DONE') {
    if (object.has_unconfirmed_persons) {
      statuses.push(
        <TooltipTrigger
          tooltipText="New persons found"
          key="face-recognition-status-done-new"
          placement="bottom"
          componentProps={{
            role: 'status',
            'aria-label': 'New persons found',
          }}
        >
          <div
            className={classNames(styles.status_icon, styles.status_warning)}
          >
            <Icon name="face_recognition" />
          </div>
        </TooltipTrigger>,
      );
    } else {
      statuses.push(
        <TooltipTrigger
          tooltipText="Face recognition completed"
          key="face-recognition-status-done-completed"
          placement="bottom"
          componentProps={{
            role: 'status',
            'aria-label': 'Face recognition completed',
          }}
        >
          <div className={styles.status_icon}>
            <Icon name="face_recognition" />
          </div>
        </TooltipTrigger>,
      );
    }
  }

  if (object.analyze_status === 'DONE') {
    statuses.push(
      <TooltipTrigger
        tooltipText="Analyzed"
        key="analyze-status-done-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Analyzed',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="analyze" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (incompleteTransferJob) {
    statuses.push(
      <TooltipTrigger
        tooltipText="Upload in progress"
        key="upload-progress-status"
        placement="bottom"
        componentProps={{
          role: 'status',
          'aria-label': 'Upload in progress',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="load_up" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (object.versions_number && object.versions_number > 1) {
    statuses.push(
      <TooltipTrigger
        tooltipText={`Version #${object.versions_number}`}
        placement="bottom"
        key="version-status"
        componentProps={{
          role: 'status',
          'aria-label': `Version #${object.versions_number}`,
        }}
      >
        <div className={styles.version}>v.{object.versions_number}</div>
      </TooltipTrigger>,
    );
  }

  if (latestVersion?.transcribe_status === 'DONE') {
    statuses.push(
      <TooltipTrigger
        tooltipText="Transcribed"
        placement="bottom"
        key="transcription-status"
        componentProps={{
          role: 'status',
          'aria-label': 'Transcribed',
        }}
      >
        <div className={styles.status_icon}>
          <Icon name="transcription" />
        </div>
      </TooltipTrigger>,
    );
  }

  if (statuses.length > 0) {
    return (
      <div className={classNames(styles.status_info, styles.status)}>
        {statuses}
      </div>
    );
  }
  return null;
};

export default InfoStatuses;
